import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Box, useMediaQuery, useTheme, Typography } from "@mui/material";

import AnimatedPhoneIcon from "./AnimatedPhone";
import AnimatedSmartWatch from "./AnimatedSmartWatch";
import BetweenVisitsAnimation from "./BetweenVisits";

export default function InsightPulseAnimation() {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const [index, setIndex] = useState(0);

  const cycle = ["watch", "phone", "alert"];
  const current = cycle[index];

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((p) => (p + 1) % cycle.length);
    }, 7500);
    return () => clearInterval(id);
  }, []);

  const captions = {
    watch: "HEALTH TELEMETRY",
    phone: "PATIENT SURVEYS",
    alert: "CARE TEAM ALERTS"
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        mt: { xs: 3, md: 4 }
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          gap: { xs: 4, sm: 7, md: 10 },
          width: "100%",
          maxWidth: 520,
          flexWrap: "nowrap"
        }}
      >
        {!(isSmall && current === "alert") && (
          <Box
            sx={{
              width: { xs: 110, sm: 130, md: 160 },
              height: { xs: 110, sm: 130, md: 160 },
              position: "relative",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <motion.div
              style={{
                position: "absolute",
                width: "70%",
                height: "70%",
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(124,58,237,0.12), rgba(124,58,237,0))"
              }}
              animate={{ scale: [1, 1.08, 1], opacity: [0.4, 0.7, 0.4] }}
              transition={{ duration: 4, repeat: Infinity }}
            />

            <motion.div
              style={{
                position: "absolute",
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                border: "2px solid rgba(124,58,237,0.15)"
              }}
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
            />

            <motion.div
              style={{
                position: "absolute",
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                border: "1px dashed rgba(100,116,139,0.20)"
              }}
              animate={{ rotate: [360, 0] }}
              transition={{ duration: 26, repeat: Infinity, ease: "linear" }}
            />

            {[0, 1, 2].map((i) => (
              <motion.div
                key={"left-" + i}
                style={{
                  position: "absolute",
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  background: "linear-gradient(90deg,#7c3aed,#3b82f6)"
                }}
                initial={{ scale: 0, opacity: 0 }}
                animate={{
                  scale: [0, 1, 0],
                  opacity: [0, 1, 0],
                  x: [100, 0],
                  y: [-40 + i * 40, 0]
                }}
                transition={{
                  duration: 3,
                  delay: i * 0.4,
                  repeat: Infinity,
                  repeatDelay: 3
                }}
              />
            ))}

            {[0, 1, 2].map((i) => (
              <motion.div
                key={"right-" + i}
                style={{
                  position: "absolute",
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: "linear-gradient(90deg,#3b82f6,#7c3aed)"
                }}
                initial={{ scale: 0, opacity: 0 }}
                animate={{
                  scale: [0, 1, 0],
                  opacity: [0, 1, 0],
                  x: [-100, 0],
                  y: [-20 + i * 20, 0]
                }}
                transition={{
                  duration: 3,
                  delay: i * 0.4 + 1,
                  repeat: Infinity,
                  repeatDelay: 3
                }}
              />
            ))}

            <motion.div
              style={{
                position: "absolute",
                width: "45%",
                height: "45%",
                borderRadius: "50%",
                background:
                  "conic-gradient(from 0deg, #7c3aed, #3b82f6, #7c3aed)"
              }}
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
            />

            <motion.div
              style={{
                position: "absolute",
                width: "48%",
                height: "48%",
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 70%)"
              }}
            />

            <motion.div
              style={{
                position: "absolute",
                width: "22%",
                height: "22%"
              }}
              animate={{ scale: [1, 1.15, 1] }}
              transition={{ duration: 4, repeat: Infinity }}
            >
              <img
                src="/assets/icon_transparent.png"
                alt="Panacea Logo"
                style={{ width: "100%", height: "100%" }}
              />
            </motion.div>
          </Box>
        )}

        <Box
          sx={{
            width: { xs: 200, sm: 220, md: 260 },
            height: { xs: 260, sm: 280, md: 320 },
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative"
          }}
        >
          <AnimatePresence mode="wait">
            {current === "watch" && (
              <motion.div
                key="watch"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.6 }}
                style={{ width: "100%", display: "flex", justifyContent: "center" }}
              >
                <AnimatedSmartWatch size={isSmall ? 120 : 160} />
              </motion.div>
            )}

            {current === "phone" && (
              <motion.div
                key="phone"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.6 }}
                style={{ width: "100%", display: "flex", justifyContent: "center" }}
              >
                <AnimatedPhoneIcon size={isSmall ? 120 : 160} />
              </motion.div>
            )}

            {current === "alert" && (
              <motion.div
                key="alert"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.6 }}
                style={{ width: "100%", display: "flex", justifyContent: "center" }}
              >
                <BetweenVisitsAnimation />
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </Box>

      {/* Caption moved closer + halo */}
      <Box sx={{ mt: 1.2, mb: 1.2, textAlign: "center" }}>
        <Typography
          variant="overline"
          sx={{
            textTransform: "uppercase",
            letterSpacing: "1.4px",
            color: "#6366f1",
            fontWeight: 600,
            opacity: 0.9,
            fontSize: { xs: "0.72rem", sm: "0.78rem" }
          }}
        >
          {captions[current]}
        </Typography>

        <Box
          sx={{
            mt: 0.4,
            width: 42,
            height: 2,
            mx: "auto",
            borderRadius: 2,
            background: "linear-gradient(90deg,#7c3aed,#3b82f6)",
            opacity: 0.55
          }}
        />
      </Box>
    </Box>
  );
}
